import { formatDateDDMMYY, formatTime24 } from '../../utils/dateFormat';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Pagination,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CodeIcon from '@mui/icons-material/Code';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ErrorIcon from '@mui/icons-material/Error';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HistoryIcon from '@mui/icons-material/History';
import PersonIcon from '@mui/icons-material/Person';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/Settings';
import StorageIcon from '@mui/icons-material/Storage';
import TerminalIcon from '@mui/icons-material/Terminal';
import WarningIcon from '@mui/icons-material/Warning';

import { useSnackbar } from '../../contexts/SnackbarContext';
import api from '../../services/apiClient';
import { getGlobalAuditLogs, AuditLogEntry } from '../../services/adminService';

const cardSx = {
  borderRadius: 0,
  border: '0.5px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
} as const;

const labelSx = {
  fontSize: '0.64rem',
  fontWeight: 800,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.08em',
  color: 'text.disabled',
};

const monoSx = {
  fontFamily: "'DM Mono', monospace",
  fontSize: '0.76rem',
  fontWeight: 800,
};

const iconSx = {
  borderRadius: 0,
  border: '0.5px solid',
  borderColor: 'divider',
  width: 30,
  height: 30,
} as const;

const validateYamlLike = (content: string) => {
  const errors: string[] = [];
  const warnings: string[] = [];
  const lines = content.split('\n');

  lines.forEach((line, index) => {
    if (!line.trim() || line.trim().startsWith('#')) return;
    if (/^\t+/.test(line)) errors.push(`Line ${index + 1}: tab indentation detected. YAML requires spaces.`);
    const trimmed = line.trim();
    const isListItem = trimmed.startsWith('- ');
    const hasColon = trimmed.includes(':');
    if (!isListItem && !hasColon && !trimmed.startsWith('{') && !trimmed.startsWith('[')) {
      warnings.push(`Line ${index + 1}: no key/value separator detected.`);
    }
    if (hasColon && /:\S/.test(trimmed) && !/^https?:/.test(trimmed)) {
      warnings.push(`Line ${index + 1}: missing space after colon may be invalid YAML.`);
    }
  });

  if (!content.trim()) errors.push('Configuration is empty.');
  if (!content.includes('paths:')) warnings.push('No paths: section detected. Confirm this is intentional.');

  return { valid: errors.length === 0, errors, warnings: warnings.slice(0, 8) };
};

const diffSummary = (original: string, current: string) => {
  const originalLines = original.split('\n');
  const currentLines = current.split('\n');
  const max = Math.max(originalLines.length, currentLines.length);
  let changed = 0;
  let added = 0;
  let removed = 0;
  const preview: string[] = [];

  for (let i = 0; i < max; i += 1) {
    const before = originalLines[i];
    const after = currentLines[i];
    if (before === after) continue;
    changed += 1;
    if (before === undefined) added += 1;
    else if (after === undefined) removed += 1;
    if (preview.length < 12) {
      preview.push(`Line ${i + 1}`);
      if (before !== undefined) preview.push(`- ${before}`);
      if (after !== undefined) preview.push(`+ ${after}`);
    }
  }

  return { changed, added, removed, preview };
};

export const SystemConfigPage: React.FC = () => {
  const theme = useTheme();
  const { showSnackbar } = useSnackbar();
  const [tabIndex, setTabIndex] = useState(0);

  const [configContent, setConfigContent] = useState('');
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [serverStatus, setServerStatus] = useState<'ONLINE' | 'OFFLINE' | 'UNKNOWN'>('UNKNOWN');
  const [configChanged, setConfigChanged] = useState(false);
  const [originalConfig, setOriginalConfig] = useState('');
  const [saveOpen, setSaveOpen] = useState(false);
  const [restartOpen, setRestartOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [actionReason, setActionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [logPage, setLogPage] = useState(1);
  const [logTotalPages, setLogTotalPages] = useState(1);
  const [logSearch, setLogSearch] = useState('');
  const [loadingLogs, setLoadingLogs] = useState(false);

  const validation = useMemo(() => validateYamlLike(configContent), [configContent]);
  const diff = useMemo(() => diffSummary(originalConfig, configContent), [originalConfig, configContent]);

  useEffect(() => {
    if (tabIndex === 0) fetchConfig();
    if (tabIndex === 1) fetchLogs();
  }, [tabIndex]);

  useEffect(() => {
    if (tabIndex !== 1) return;
    const timer = setTimeout(() => {
      setLogPage(1);
      fetchLogs();
    }, 500);
    return () => clearTimeout(timer);
  }, [logSearch]);

  useEffect(() => {
    if (tabIndex === 1) fetchLogs();
  }, [logPage]);

  const fetchConfig = async () => {
    setLoadingConfig(true);
    try {
      const res = await api.get('/admin/mediamtx/config');
      setConfigContent(res.data);
      setOriginalConfig(res.data);
      setConfigChanged(false);
      api.get('/admin/active-streams').then(() => setServerStatus('ONLINE')).catch(() => setServerStatus('OFFLINE'));
    } catch (e) {
      showSnackbar('Failed to load config', 'error');
    } finally {
      setLoadingConfig(false);
    }
  };

  const saveConfig = async () => {
    setActionLoading(true);
    try {
      await api.post('/admin/mediamtx/config', { content: configContent, reason: actionReason });
      showSnackbar('Configuration saved to disk', 'success');
      setOriginalConfig(configContent);
      setConfigChanged(false);
      setSaveOpen(false);
      setConfirmText('');
      setActionReason('');
    } catch (e) {
      showSnackbar('Failed to write config file', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const restartService = async () => {
    setActionLoading(true);
    try {
      await api.post('/admin/mediamtx/restart', { reason: actionReason });
      showSnackbar('Restart command sent', 'info');
      setRestartOpen(false);
      setConfirmText('');
      setActionReason('');
      setTimeout(fetchConfig, 3000);
    } catch (e) {
      showSnackbar('Restart command failed', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const fetchLogs = async () => {
    setLoadingLogs(true);
    try {
      const data = await getGlobalAuditLogs(logPage, 50, logSearch);
      setLogs(data.logs);
      setLogTotalPages(data.totalPages);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleConfigChange = (value: string) => {
    setConfigContent(value);
    setConfigChanged(value !== originalConfig);
  };

  const handleResetConfig = () => {
    setConfigContent(originalConfig);
    setConfigChanged(false);
    showSnackbar('Rolled back editor to last loaded config', 'info');
  };

  const copyBackup = async () => {
    await navigator.clipboard.writeText(originalConfig);
    showSnackbar('Last loaded config copied as rollback backup', 'success');
  };

  const getActionColor = (action: string) => {
    const actionLower = action.toLowerCase();
    if (actionLower.includes('delete') || actionLower.includes('remove') || actionLower.includes('kill')) return 'error';
    if (actionLower.includes('create') || actionLower.includes('approve')) return 'success';
    if (actionLower.includes('update') || actionLower.includes('modify')) return 'warning';
    return 'default';
  };

  return (
    <Box sx={{ maxWidth: 1800, mx: 'auto' }}>
      <Paper elevation={0} sx={{ ...cardSx, p: 1.5, mb: 1.5 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', lg: 'center' }}>
          <Stack direction="row" spacing={2.2} alignItems="center" flexWrap="wrap" useFlexGap>
            <Stack direction="row" spacing={1} alignItems="center">
              <SettingsIcon sx={{ color: 'text.secondary' }} />
              <Box>
                <Typography sx={{ fontSize: '1rem', fontWeight: 900 }}>System Administration</Typography>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Media engine config, safe restarts, and audit logs</Typography>
              </Box>
            </Stack>
            <Box sx={{ borderLeft: `3px solid ${serverStatus === 'ONLINE' ? theme.palette.success.main : serverStatus === 'OFFLINE' ? theme.palette.error.main : theme.palette.info.main}`, pl: 1.25 }}>
              <Typography sx={labelSx}>Engine</Typography>
              <Typography sx={{ fontWeight: 900 }}>{serverStatus}</Typography>
            </Box>
            <Box sx={{ borderLeft: `3px solid ${configChanged ? theme.palette.warning.main : theme.palette.success.main}`, pl: 1.25 }}>
              <Typography sx={labelSx}>Config</Typography>
              <Typography sx={{ fontWeight: 900 }}>{configChanged ? 'MODIFIED' : 'SAVED'}</Typography>
            </Box>
            <Box sx={{ borderLeft: `3px solid ${validation.valid ? theme.palette.success.main : theme.palette.error.main}`, pl: 1.25 }}>
              <Typography sx={labelSx}>Local Check</Typography>
              <Typography sx={{ fontWeight: 900 }}>{validation.valid ? 'PASS' : 'FAIL'}</Typography>
            </Box>
            <Box sx={{ borderLeft: `3px solid ${theme.palette.info.main}`, pl: 1.25 }}>
              <Typography sx={labelSx}>Diff</Typography>
              <Typography sx={{ fontWeight: 900 }}>{diff.changed} lines</Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={0.75} justifyContent="flex-end">
            <Tooltip title="Copy rollback backup"><IconButton size="small" onClick={copyBackup} disabled={!originalConfig} sx={iconSx}><ContentCopyIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
            <Tooltip title="Reload config"><IconButton size="small" onClick={fetchConfig} disabled={loadingConfig} sx={iconSx}><RestartAltIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
          </Stack>
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ ...cardSx, mb: 1.5 }}>
        <Tabs value={tabIndex} onChange={(e, v) => setTabIndex(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 1, '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, minHeight: 52 } }}>
          <Tab label="Media Engine" icon={<TerminalIcon />} iconPosition="start" />
          <Tab label="Global Audit Logs" icon={<HistoryIcon />} iconPosition="start" />
        </Tabs>

        <Box sx={{ p: 1.5 }}>
          {tabIndex === 0 && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1.65fr 0.8fr' }, gap: 1.5 }}>
              <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
                <Box sx={{ p: 1.25, borderBottom: '0.5px solid', borderColor: 'divider', bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CodeIcon color="primary" />
                      <Typography sx={{ ...monoSx, color: 'text.secondary' }}>/etc/mediamtx/mediamtx.yml</Typography>
                      {configChanged && <Chip label="UNSAVED" size="small" color="warning" sx={{ borderRadius: 0, fontWeight: 800, height: 22 }} />}
                    </Stack>
                    <Stack direction="row" spacing={0.75}>
                      <Button size="small" variant="outlined" startIcon={<RestartAltIcon />} onClick={handleResetConfig} disabled={!configChanged || loadingConfig} sx={{ borderRadius: 0, textTransform: 'none', fontWeight: 800 }}>Rollback editor</Button>
                      <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={() => { setConfirmText(''); setActionReason(''); setSaveOpen(true); }} disabled={loadingConfig || !configChanged || !validation.valid} sx={{ borderRadius: 0, textTransform: 'none', fontWeight: 800 }}>Save config</Button>
                    </Stack>
                  </Stack>
                </Box>

                {!validation.valid && <Alert severity="error" sx={{ borderRadius: 0 }}>{validation.errors[0]}</Alert>}
                {configChanged && validation.valid && <Alert severity="warning" sx={{ borderRadius: 0 }}>Unsaved config changes detected. Review diff and save only after confirming syntax and operational impact.</Alert>}

                {loadingConfig ? (
                  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={12}><CircularProgress size={34} sx={{ mb: 2 }} /><Typography color="text.secondary">Loading configuration...</Typography></Box>
                ) : (
                  <TextField
                    multiline
                    fullWidth
                    variant="outlined"
                    value={configContent}
                    onChange={(e) => handleConfigChange(e.target.value)}
                    placeholder="# MediaMTX Configuration"
                    inputProps={{ style: { fontFamily: '"Fira Code", "Cascadia Code", "Consolas", monospace', fontSize: '13px', lineHeight: '1.55', minHeight: '610px', padding: '16px' } }}
                    sx={{ bgcolor: 'background.default', '& .MuiOutlinedInput-root': { padding: 0, borderRadius: 0 }, '& .MuiOutlinedInput-notchedOutline': { border: 'none' } }}
                  />
                )}
                <Box sx={{ p: 1.15, borderTop: '0.5px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', bgcolor: alpha(theme.palette.background.default, 0.5) }}>
                  <Typography sx={{ ...monoSx, color: 'text.secondary' }}>Lines: {configContent.split('\n').length} · Characters: {configContent.length}</Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>YAML local check: {validation.valid ? 'pass' : 'fail'}</Typography>
                </Box>
              </Paper>

              <Stack spacing={1.5}>
                <Paper elevation={0} sx={{ ...cardSx, p: 1.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}><SecurityIcon sx={{ color: 'text.secondary' }} /><Typography sx={{ fontSize: '0.9rem', fontWeight: 900 }}>Safety Checks</Typography></Stack>
                  <Stack spacing={1}>
                    <Chip label={validation.valid ? 'Local YAML check passed' : 'Local YAML check failed'} color={validation.valid ? 'success' : 'error'} icon={validation.valid ? <CheckCircleIcon /> : <ErrorIcon />} sx={{ borderRadius: 0, justifyContent: 'flex-start' }} />
                    {validation.errors.map((item) => <Alert key={item} severity="error" sx={{ borderRadius: 0 }}>{item}</Alert>)}
                    {validation.warnings.map((item) => <Alert key={item} severity="warning" sx={{ borderRadius: 0 }}>{item}</Alert>)}
                  </Stack>
                </Paper>

                <Paper elevation={0} sx={{ ...cardSx, p: 1.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}><HistoryIcon sx={{ color: 'text.secondary' }} /><Typography sx={{ fontSize: '0.9rem', fontWeight: 900 }}>Diff Summary</Typography></Stack>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                    <Chip label={`${diff.changed} changed`} size="small" sx={{ borderRadius: 0 }} />
                    <Chip label={`${diff.added} added`} size="small" sx={{ borderRadius: 0 }} />
                    <Chip label={`${diff.removed} removed`} size="small" sx={{ borderRadius: 0 }} />
                  </Stack>
                  <Box sx={{ bgcolor: 'background.default', p: 1, maxHeight: 220, overflow: 'auto', border: '0.5px solid', borderColor: 'divider' }}>
                    {diff.preview.length === 0 ? <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>No changes from loaded config.</Typography> : diff.preview.map((line, i) => <Typography key={`${line}-${i}`} sx={{ ...monoSx, color: line.startsWith('+') ? 'success.main' : line.startsWith('-') ? 'error.main' : 'text.secondary', whiteSpace: 'pre-wrap' }}>{line}</Typography>)}
                  </Box>
                </Paper>

                <Paper elevation={0} sx={{ ...cardSx, p: 1.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}><StorageIcon sx={{ color: 'text.secondary' }} /><Typography sx={{ fontSize: '0.9rem', fontWeight: 900 }}>Service Control</Typography></Stack>
                  <Stack spacing={1}>
                    <Box display="flex" justifyContent="space-between" alignItems="center"><Typography sx={{ fontSize: '0.78rem', fontWeight: 800 }}>Engine State</Typography><Chip label={serverStatus} color={serverStatus === 'ONLINE' ? 'success' : serverStatus === 'OFFLINE' ? 'error' : 'default'} size="small" sx={{ borderRadius: 0, fontWeight: 800 }} /></Box>
                    <Button variant="outlined" color="error" startIcon={<RestartAltIcon />} onClick={() => { setConfirmText(''); setActionReason(''); setRestartOpen(true); }} fullWidth sx={{ borderRadius: 0, textTransform: 'none', fontWeight: 800 }}>Restart Media Engine</Button>
                    <Alert severity="info" sx={{ borderRadius: 0 }}>Restarting disconnects active streams. Use a reason and typed confirmation.</Alert>
                  </Stack>
                </Paper>
              </Stack>
            </Box>
          )}

          {tabIndex === 1 && (
            <Box>
              <Paper elevation={0} sx={{ ...cardSx, p: 1.25, mb: 1.5 }}>
                <TextField size="small" placeholder="Search by action, details, or user ID..." value={logSearch} onChange={(e) => setLogSearch(e.target.value)} fullWidth InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>, sx: { borderRadius: 0 } }} />
              </Paper>
              <TableContainer component={Paper} elevation={0} sx={cardSx}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}><TableRow><TableCell sx={labelSx}>Timestamp</TableCell><TableCell sx={labelSx}>Actor</TableCell><TableCell sx={labelSx}>Action</TableCell><TableCell sx={labelSx}>Details</TableCell></TableRow></TableHead>
                  <TableBody>
                    {loadingLogs ? <TableRow><TableCell colSpan={4} align="center" sx={{ py: 7 }}><CircularProgress size={28} /></TableCell></TableRow> : logs.length === 0 ? <TableRow><TableCell colSpan={4} align="center" sx={{ py: 7 }}><HistoryIcon sx={{ fontSize: 42, color: 'text.disabled', mb: 1 }} /><Typography color="text.secondary">No audit logs found</Typography></TableCell></TableRow> : logs.map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}><Typography sx={monoSx}>{formatDateDDMMYY(log.createdAt)}</Typography><Typography sx={{ ...monoSx, color: 'text.secondary' }}>{formatTime24(log.createdAt)}</Typography></TableCell>
                        <TableCell>{log.user ? <Stack direction="row" alignItems="center" spacing={1}><PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} /><Box><Typography sx={{ fontSize: '0.78rem', fontWeight: 800 }}>{log.user.email}</Typography><Typography sx={{ ...monoSx, color: 'text.disabled' }}>ID {log.userId}</Typography></Box></Stack> : <Stack direction="row" alignItems="center" spacing={1}><AdminPanelSettingsIcon sx={{ fontSize: 16, color: 'text.secondary' }} /><Typography sx={{ fontSize: '0.74rem', color: 'text.secondary' }}>System/Unknown</Typography></Stack>}</TableCell>
                        <TableCell><Chip label={log.action} size="small" color={getActionColor(log.action) as any} sx={{ borderRadius: 0, fontWeight: 800, fontSize: '0.66rem' }} /></TableCell>
                        <TableCell><Typography sx={{ fontSize: '0.76rem' }}>{log.details}</Typography></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {!loadingLogs && logs.length > 0 && <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}><Pagination count={logTotalPages} page={logPage} onChange={(e, v) => setLogPage(v)} color="primary" shape="rounded" sx={{ '& .MuiPaginationItem-root': { borderRadius: 0 } }} /></Box>}
            </Box>
          )}
        </Box>
      </Paper>

      <Dialog open={saveOpen} onClose={() => setSaveOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 0 } }}>
        <DialogTitle>Save Media Engine Config</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 0 }}>This writes the config to disk. Invalid YAML may prevent the engine from restarting.</Alert>
          <Stack spacing={1.5}>
            <TextField label="Reason" fullWidth multiline minRows={3} value={actionReason} onChange={(e) => setActionReason(e.target.value)} InputProps={{ sx: { borderRadius: 0 } }} />
            <TextField label="Type SAVE CONFIG to confirm" fullWidth value={confirmText} onChange={(e) => setConfirmText(e.target.value)} InputProps={{ sx: { borderRadius: 0 } }} />
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setSaveOpen(false)}>Cancel</Button><Button variant="contained" color="warning" disabled={actionLoading || confirmText !== 'SAVE CONFIG' || !actionReason.trim() || !validation.valid} onClick={saveConfig}>{actionLoading ? 'Saving...' : 'Save Config'}</Button></DialogActions>
      </Dialog>

      <Dialog open={restartOpen} onClose={() => setRestartOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 0 } }}>
        <DialogTitle>Restart Media Engine</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2, borderRadius: 0 }}>This temporarily disconnects active inputs and outputs. Do not restart during critical events unless necessary.</Alert>
          <Stack spacing={1.5}>
            <TextField label="Reason" fullWidth multiline minRows={3} value={actionReason} onChange={(e) => setActionReason(e.target.value)} InputProps={{ sx: { borderRadius: 0 } }} />
            <TextField label="Type RESTART ENGINE to confirm" fullWidth value={confirmText} onChange={(e) => setConfirmText(e.target.value)} InputProps={{ sx: { borderRadius: 0 } }} />
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setRestartOpen(false)}>Cancel</Button><Button variant="contained" color="error" disabled={actionLoading || confirmText !== 'RESTART ENGINE' || !actionReason.trim()} onClick={restartService}>{actionLoading ? 'Restarting...' : 'Restart Engine'}</Button></DialogActions>
      </Dialog>
    </Box>
  );
};
